import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';

import { User } from '../models/user.model';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private accessTokenKey: string = environment.AccessTokenKeyLabel;
  private refreshTokenKey: string = environment.RefreshTokenKeyLabel;
  private currentUserKey: string = environment.CurrentUserKey;

  private currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User>;

  public user = new User();

  public header = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
  };

  constructor(private http: HttpClient) {
    let hbCurrentUser: any = localStorage.getItem(this.currentUserKey);
    if (hbCurrentUser) {
      this.currentUserSubject = new BehaviorSubject<User>(
        JSON.parse(hbCurrentUser)
      );
    } else {
      this.currentUserSubject = new BehaviorSubject<User>(new User());
    }
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get CurrentUserValue() {
    return this.currentUserSubject.value;
  }

  forgotPassword(credentialInfo: any): Observable<any> {
    return new Observable((observer: any) => {
      this.http
        .post(
          `${environment.AuthApiEndPoint}/forgotpassword`,
          credentialInfo,
          this.header
        )
        .subscribe(
          (response: any) => {
            observer.next(response);
            observer.complete();
          },
          (error) => {
            observer.error(error);
          }
        );
    });
  }

  authenticate(credentialInfo: any, projectInfo: any = null): Observable<any> {
    return new Observable((observer: any) => {
      this.http
        .post(
          `${environment.AuthApiEndPoint}/authenticate`,
          credentialInfo,
          this.header
        )
        .subscribe(
          (response: any) => {
            if (response.success) {
              /*if (!response.UserInfo.ProfilePictureOriginalFilename) {
              //response.ProfilePictureFilename = `${environment.SiteUrl}${environment.DefaultImagePlaceHolder}`;
              response.ProfilePictureOriginalFilename = '';
              }*/
              if (projectInfo?.project_id) {
                // this is comming from interceptor
                response.data.userData.project_id = projectInfo.project_id;
                response.data.userData.project_name = projectInfo.project_name;
              }
              this.saveLoggedUserToLocalStorage(response.data.userData);
              this.saveToken(response.data.token, true);
            }
            observer.next(response);
            observer.complete();
          },
          (error) => {
            console.log(error);
            localStorage.removeItem(this.currentUserKey);
            this.currentUserSubject.next(new User());
            observer.error(error);
          }
        );
    });
  }

  googleAuthenticate(credentialInfo: any): Observable<any> {
    return new Observable((observer: any) => {
      this.http
        .post(
          `${environment.AuthApiEndPoint}/googleAuthenticate`,
          credentialInfo,
          this.header
        )
        .subscribe(
          (response: any) => {
            if (response.success) {
              this.saveLoggedUserToLocalStorage(response.data.userData);
              this.saveToken(response.data.token, true);
            }
            observer.next(response);
            observer.complete();
          },
          (error) => {
            console.log(error);
            localStorage.removeItem(this.currentUserKey);
            this.currentUserSubject.next(new User());
            observer.error(error);
          }
        );
    });
  }

  authenticateToAlertPage(credentialInfo: any): Observable<any> {
    return new Observable((observer: any) => {
      this.http
        .post(
          environment.AuthApiEndPoint + `/authenticateToAlertPage`,
          credentialInfo,
          this.header
        )
        .subscribe(
          (response: any) => {
            observer.next(response);
            observer.complete();
          },
          (error) => {
            observer.error(error);
          }
        );
    });
  }
  //userLogout(userId: String): Observable<any> {
  userLogout(userId: string): Observable<any> {
    return new Observable((observer: any) => {
      this.http
        //.put(environment.UserApiEndPoint + `/logout`, { UserId: userId }, this.header)
        .put(
          `${environment.AuthApiEndPoint}/logout`,
          { user_id: userId },
          this.header
        )
        .subscribe(
          (response: any) => {
            this.removeTokens(true);
            this.currentUserSubject.next(new User());
            observer.next(response);
            observer.complete();
          },
          (error) => {
            this.removeTokens(true);
            this.currentUserSubject.next(new User());
            observer.error(error);
          }
        );
    });
  }

  //#region Working with tokens
  saveToken(token: any, isSetRefreshToken: Boolean = false) {
    window.localStorage.removeItem(this.accessTokenKey);
    window.localStorage.setItem(this.accessTokenKey, token.access);
    if (isSetRefreshToken) {
      window.localStorage.removeItem(this.refreshTokenKey);
      window.localStorage.setItem(this.refreshTokenKey, token.refresh);
    }
  }
  getToken(tokenKey: string): string | null {
    return window.localStorage.getItem(tokenKey);
  }
  removeTokens(wantToRemoveLocalStorage: Boolean = false): void {
    window.localStorage.removeItem(this.accessTokenKey);
    window.localStorage.removeItem(this.refreshTokenKey);
    if (wantToRemoveLocalStorage) {
      localStorage.removeItem(this.currentUserKey);
    }
  }
  saveLoggedUserToLocalStorage(loggedUser: User): void {
    this.user.id = loggedUser.id;
    this.user.first_name = loggedUser.first_name;
    this.user.full_name = `${loggedUser.first_name} ${
      loggedUser.last_name ? loggedUser.last_name : ''
    }`;
    this.user.last_name = loggedUser.last_name;
    this.user.email = loggedUser.email;
    this.user.role_id = loggedUser.role_id;
    this.user.profile_pic = loggedUser.profile_pic
      ? environment.AwsS3BucketUrl + loggedUser.profile_pic
      : environment.DefaultProfileImage;
    this.user.role = loggedUser.role;
    this.user.project_id = loggedUser.project_id ? loggedUser.project_id : '';
    this.user.project_name = loggedUser.project_name
      ? loggedUser.project_name
      : '';
    this.user.project_manager_id = loggedUser.project_manager_id
      ? loggedUser.project_manager_id
      : '';
    localStorage.setItem(this.currentUserKey, JSON.stringify(this.user));
    this.currentUserSubject.next(this.user);
  }
  //#endregion

  //#region These two functions are for reflect the changes on page header while user editing his profile
  observeLoggedUser(): Observable<any> {
    return this.currentUserSubject.asObservable();
  }

  setLoggedUserInfoToLocalStorage(
    userInfo: User,
    returnAsAdmin: Boolean = false
  ) {
    // Checking for impersonate users. If so, it's attached to local storage
    let currentUser: User = this.CurrentUserValue;

    /*if (!returnAsAdmin) {
         if (currentUser.ImpersonatedBy && currentUser.ImpersonatedBy._id) {
            userInfo.ImpersonatedBy = currentUser.ImpersonatedBy;
         }
      }*/

    localStorage.setItem(this.currentUserKey, JSON.stringify(userInfo));
    this.currentUserSubject.next(userInfo);
  }
  //#endregion

  resetPassword(credentialInfo: any): Observable<any> {
    return new Observable((observer: any) => {
      this.http
        .put(
          environment.AuthApiEndPoint + `/resetPassword`,
          credentialInfo,
          this.header
        )
        .subscribe(
          (response: any) => {
            observer.next(response);
            observer.complete();
          },
          (error) => {
            observer.error(error);
          }
        );
    });
  }

  isLinkExpired(token: String): Observable<any> {
    return new Observable((observer) => {
      this.http
        .post(
          environment.AuthApiEndPoint + `/isLinkExpired`,
          { token },
          this.header
        )
        .subscribe(
          (response: any) => {
            observer.next(response);
            observer.complete();
          },
          (error) => {
            observer.error(error);
          }
        );
    });
  }

  /*sendSupportMessage(messageInfo: any): Observable<any> {
    let headers = new HttpHeaders();
    headers.append('Content-Type', 'multipart/form-data');
    headers.append('Accept', 'application/json');

    return new Observable((observer: any) => {
      this.http
        .post(
          environment.AuthApiEndPoint + `/sendSupportMessage`,
          messageInfo,
          { headers }
        )
        .subscribe(
          (response: any) => {
            observer.next(response);
            observer.complete();
          },
          (error) => {
            observer.error(error);
          }
        );
    });
  }*/
}
